/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/ui/InstallPrompt.tsx
 *
 * Rôle :
 *   Bannière d'INVITATION À INSTALLER la PWA (demande Mika 2026-05-18 :
 *   « aucun message d'installation sur Android ; sur Apple un message
 *   qui propose d'installer + comment ; sur PC/Mac une installation »).
 *
 *   - Android / Chrome / Edge (mobile & desktop) → bouton « Installer
 *     l'application » qui déclenche l'invite native (beforeinstallprompt).
 *   - iOS / iPadOS Safari → message + lien vers la marche à suivre
 *     (/install-help-ios, page déjà existante avec captures pas-à-pas).
 *   - Déjà installée / refusée → rien (composant null).
 *
 *   Visuel REPRIS du pattern `OfflineBanner` (sticky sous le Header,
 *   même structure, aucun design inventé §8.6). Accessible
 *   (`role="region"`, bouton fermeture `aria-label`).
 *
 * @author Mika
 * @since  2026-05-18
 */

import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEventIdentity } from '@/context/EventIdentityProvider';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { toast } from '@/stores/toast';
import styles from './InstallPrompt.module.css';

export function InstallPrompt() {
  const intl = useIntl();
  const identity = useEventIdentity();
  const { status, promptInstall, dismiss, dismissed } = usePwaInstall();

  // Rien à proposer : installée, refusée, ou navigateur non éligible.
  if (status === 'installed' || status === 'unsupported' || dismissed) {
    return null;
  }

  const onInstall = (): void => {
    void promptInstall().then((outcome) => {
      if (outcome === 'accepted') {
        toast.success(intl.formatMessage({ id: 'pwa.install.accepted' }));
      } else if (outcome === 'unavailable') {
        toast.info(intl.formatMessage({ id: 'pwa.install.unavailable' }));
      }
      // 'dismissed' : l'utilisateur a refusé l'invite native, silencieux.
    });
  };

  return (
    <div
      className={styles.banner}
      role="region"
      aria-label={intl.formatMessage({ id: 'pwa.install.aria' }, identity)}
    >
      <span className={styles.icon} aria-hidden="true">
        📲
      </span>
      <span className={styles.text}>
        {status === 'ios' ? (
          <FormattedMessage id="pwa.install.iosText" values={identity} />
        ) : (
          <FormattedMessage id="pwa.install.text" values={identity} />
        )}
      </span>

      {status === 'ios' ? (
        <Link to="/install-help-ios" className={styles.cta}>
          <FormattedMessage id="pwa.install.iosCta" />
        </Link>
      ) : (
        <button type="button" className={styles.cta} onClick={onInstall}>
          <FormattedMessage id="btn.installApp" />
        </button>
      )}

      <button
        type="button"
        className={styles.close}
        onClick={dismiss}
        aria-label={intl.formatMessage({ id: 'pwa.install.dismiss' })}
      >
        <span aria-hidden="true">✕</span>
      </button>
    </div>
  );
}
